/**
 * Candy Animations Registry
 * 
 * Central registry for candy-specific reveal animations.
 * Each candy type has a unique animation that scales with version tier.
 */

import CocoaPopAnimation from './CocoaPopAnimation';
import SlapTaffyAnimation from './SlapTaffyAnimation';
import NutPopAnimation from './NutPopAnimation';
import ShaftBerryAnimation from './ShaftBerryAnimation';
import DryPullAnimation from './DryPullAnimation';
import './shared.css';

// Animation component registry
const CANDY_ANIMATIONS = {
    CocoaPop: CocoaPopAnimation,
    SlapTaffy: SlapTaffyAnimation,
    NutPop: NutPopAnimation,
    ShaftBerry: ShaftBerryAnimation,
};

// Version tier to intensity mapping (1-6 scale)
const VERSION_TIERS = {
    Street: 1,
    Juiced: 2,
    Cracked: 3,
    Unhinged: 4,
    GoldMember: 5,
    MythDck: 6,
};

/**
 * Get the animation component for a specific candy type
 * @param {string} candyName - Name of the candy (CocoaPop, SlapTaffy, etc.)
 * @returns {React.Component|null} Animation component or null if not found
 */
export const getCandyAnimation = (candyName) => {
    return CANDY_ANIMATIONS[candyName] || null;
};

/**
 * Get animation intensity level (1-6) from version/rarity name
 * @param {string} version - Version tier name (Street, Juiced, etc.)
 * @returns {number} Intensity level 1-6
 */
export const getAnimationIntensity = (version) => {
    return VERSION_TIERS[version] || 1;
};

/**
 * Check if a candy should trigger the DryPull (empty) animation
 * @param {object|null} reward - The reward object
 * @returns {boolean} True if this is a dry pull (no reward)
 */
export const isDryPull = (reward) => {
    return !reward || reward === null;
};

/**
 * Get the appropriate animation component based on reward
 * @param {object|null} reward - The reward object with name and rarity/version
 * @returns {{ Component: React.Component, intensity: number }}
 */
export const getRewardAnimation = (reward) => {
    if (isDryPull(reward)) {
        return {
            Component: DryPullAnimation,
            intensity: 1,
            candyType: 'DryPull',
        };
    }

    const version = reward.version || reward.rarity || 'Street';
    const intensity = getAnimationIntensity(version);
    const Component = getCandyAnimation(reward.name) || null;

    return {
        Component,
        intensity,
        candyType: reward.name,
    };
};

export {
    CocoaPopAnimation,
    SlapTaffyAnimation,
    NutPopAnimation,
    ShaftBerryAnimation,
    DryPullAnimation,
};

export default {
    getCandyAnimation,
    getAnimationIntensity,
    isDryPull,
    getRewardAnimation,
};
